import { createClient } from 'graphql-ws';

import { getToken } from 'approot/shared/api/auth/auth';
import { WEBSOCKET_ENDPOINT } from 'webclient.constants';
import { setWsConnectionStatus } from './ws-apollo';
import {
  getStudentToken,
  getStudentUserToken,
} from 'approot/student-class-to-do/student-class-to-do.utils';
import { logException } from 'approot/shared/debug';

function createWsClient() {
  let activeSocket: WebSocket;
  let pingPongTimeout: number | undefined = undefined;
  let client: ReturnType<typeof createClient>;
  let lastVisibilityState = !document.hidden;

  // Handle page visibility changes for sleep/wake scenarios
  const handleVisibilityChange = () => {
    const isCurrentlyVisible = !document.hidden;

    // Page became visible after being hidden (potential wake from sleep)
    if (isCurrentlyVisible && !lastVisibilityState) {
      console.debug('ws: page became visible, checking connection health');

      // Check if WebSocket is in a bad state and force reconnection
      if (activeSocket && activeSocket.readyState !== WebSocket.OPEN) {
        console.debug('ws: forcing reconnection after visibility change');
        setWsConnectionStatus(false, 'visibility_change');
        client?.terminate();
      }
    }

    lastVisibilityState = isCurrentlyVisible;
  };

  // Handle network online events
  const handleOnlineEvent = () => {
    console.debug('ws: network came online, checking connection health');

    // If we're offline when network comes back, force reconnection
    if (activeSocket && activeSocket.readyState !== WebSocket.OPEN) {
      console.debug('ws: forcing reconnection after coming online');
      setWsConnectionStatus(false, 'network_online');
      client?.terminate();
    }
  };

  client = createClient({
    on: {
      opened: socket => {
        activeSocket = socket as WebSocket;

        // Set up event listeners when connection opens
        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('online', handleOnlineEvent);
      },
      connecting: () => {
        console.debug('ws: connecting');
      },
      connected: () => {
        console.debug('ws: onConnected');
        setWsConnectionStatus(true);
      },
      error: error => {
        console.debug('ws: onError', new Date(), error);
        logException({
          sourceFile: 'ws-client.tsx',
          message: 'WebSocket connection error',
          e: error,
        });
        setWsConnectionStatus(false, 'error');
      },
      closed: () => {
        console.debug('ws: onDisconnected');
        setWsConnectionStatus(false);
      },
      ping: (received: boolean) => {
        if (!received) {
          pingPongTimeout = window.setTimeout(() => {
            if (activeSocket.readyState === WebSocket.OPEN) {
              console.debug('ws: ping timeout, closing connection');
              setWsConnectionStatus(false, 'ping_timeout');
              activeSocket.close();
            }
          }, 5_000);
        }
      },
      pong: (received: boolean) => {
        if (received) {
          clearTimeout(pingPongTimeout);
        }
      },
    },
    url: WEBSOCKET_ENDPOINT,
    lazy: true,
    shouldRetry: () => true,
    keepAlive: 10_000,
    connectionParams: () => {
      const classToDoToken = getStudentToken();
      const studentUserToken = getStudentUserToken();
      const token = getToken();

      if (!token && !classToDoToken && !studentUserToken) {
        setWsConnectionStatus(false);
        // this is caught by the SubscriptionClient and will trigger connection_error
        throw new Error('No auth token available on client');
      }

      return {
        Headers: {
          Authorization: `Bearer ${
            studentUserToken || classToDoToken || token
          }`,
        },
      };
    },
  });

  return client;
}

export const wsClient = createWsClient();
